// src/components/Dashboard/TaskSummaryCards.tsx
import { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Grid, CircularProgress, Alert } from '@mui/material';
import { Warning, CalendarToday, Sync, Assignment } from '@mui/icons-material';
import { getTaskSummary, TaskSummaryFilters, TaskSummaryResponse, DashboardApiError } from '../../services/dashboardApi';
import { isIntegerUserId } from '../../services/authApi';
import { useModalOpen } from '../../contexts/ModalOpenContext';

type SummaryCardType = 'total' | 'pastDue' | 'upcoming' | 'inProgress';

interface TaskSummaryCardsProps {
  type: 'user' | 'airport' | 'system';
  userId?: number | string; // May be GUID (string) or integer (number)
  airportId?: number;
  onFilterChange?: (filter: { pastDueOnly?: boolean; upcomingDays?: number; status?: string; myInProgressActionsOnly?: boolean }) => void;
  /** Which filter card to activate on first render (default: 'total') */
  defaultActiveFilter?: 'total' | 'pastDue' | 'upcoming' | 'inProgress';
  /** When true, the In Progress filter card will also set myInProgressActionsOnly */
  myInProgressActionsOnly?: boolean;
  /** Omit these cards (e.g. Tenant/Client home: total + upcoming) */
  hiddenCardTypes?: ('total' | 'upcoming')[];
}

function effectiveDefaultFilter(
  defaultActiveFilter: SummaryCardType,
  hiddenCardTypes?: ('total' | 'upcoming')[]
): SummaryCardType {
  if (!hiddenCardTypes?.length) return defaultActiveFilter;
  if (hiddenCardTypes.includes('total') && defaultActiveFilter === 'total') return 'inProgress';
  if (hiddenCardTypes.includes('upcoming') && defaultActiveFilter === 'upcoming') return 'inProgress';
  return defaultActiveFilter;
}

export default function TaskSummaryCards({
  type,
  userId,
  airportId,
  onFilterChange,
  defaultActiveFilter = 'total',
  myInProgressActionsOnly = false,
  hiddenCardTypes,
}: TaskSummaryCardsProps) {
  const { isModalOpen } = useModalOpen();
  const [summary, setSummary] = useState<TaskSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<SummaryCardType>(() =>
    effectiveDefaultFilter(defaultActiveFilter, hiddenCardTypes)
  );
  const [initialFilterFired, setInitialFilterFired] = useState(false);

  const hideTotal = Boolean(hiddenCardTypes?.includes('total'));
  const hideUpcoming = Boolean(hiddenCardTypes?.includes('upcoming'));
  const visibleCardCount = 4 - (hideTotal ? 1 : 0) - (hideUpcoming ? 1 : 0);
  const cardGridSize = visibleCardCount <= 2 ? { xs: 6 as const, sm: 6 as const } : { xs: 6 as const, sm: 3 as const };

  useEffect(() => {
    const loadSummary = async () => {
      try {
        setLoading(true);
        setError(null);

        // For airport type, wait for airportId to be available
        if (type === 'airport' && !airportId) {
          // Don't show error immediately - airportId might still be loading
          // Keep loading state until airportId is available
          setLoading(true);
          return;
        }

        const filters: TaskSummaryFilters = {};
        if (type === 'user' && userId && isIntegerUserId(userId)) {
          filters.userId = userId;
        } else if (type === 'user' && userId && typeof userId === 'string') {
          // userId is a GUID - backend should return integer userId, but fell back to GUID
          // This can happen if user doesn't exist in Users table (fallback behavior)
          setError('Task summary unavailable: userId format not supported (GUID fallback)');
          setLoading(false);
          return;
        } else if (type === 'airport' && airportId) {
          filters.airportId = airportId;
        }
        // System-wide: no filters

        const response = await getTaskSummary(filters);
        setSummary(response);
      } catch (err) {
        const apiError = err as DashboardApiError;
        setError(apiError.message || 'Failed to load task summary');
        console.error('Error loading task summary:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSummary();

    // Auto-refresh every 2 minutes; Sprint B1: skip when a modal is open
    if (!(type === 'airport' && !airportId)) {
      const interval = setInterval(() => {
        if (!isModalOpen) loadSummary();
      }, 2 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [type, userId, airportId, isModalOpen]);

  // Fire initial filter on mount if effective default is not 'total'
  const effectiveDefault = effectiveDefaultFilter(defaultActiveFilter, hiddenCardTypes);
  useEffect(() => {
    if (!initialFilterFired && onFilterChange && effectiveDefault !== 'total') {
      setInitialFilterFired(true);
      handleCardClick(effectiveDefault);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCardClick = (filterType: SummaryCardType) => {
    setActiveFilter(filterType);
    
    if (onFilterChange) {
      switch (filterType) {
        case 'total':
          onFilterChange({ pastDueOnly: undefined, upcomingDays: undefined, status: undefined, myInProgressActionsOnly: undefined });
          break;
        case 'pastDue':
          onFilterChange({ pastDueOnly: true, upcomingDays: undefined, status: undefined, myInProgressActionsOnly: undefined });
          break;
        case 'upcoming':
          onFilterChange({ pastDueOnly: undefined, upcomingDays: 7, status: undefined, myInProgressActionsOnly: undefined });
          break;
        case 'inProgress':
          onFilterChange({
            pastDueOnly: undefined,
            upcomingDays: undefined,
            status: 'IN_PROGRESS',
            myInProgressActionsOnly: myInProgressActionsOnly || undefined,
          });
          break;
      }
    }
  };

  if (loading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: visibleCardCount }, (_, i) => (
          <Grid item {...cardGridSize} key={i}>
            <Card>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No task summary data available
      </Alert>
    );
  }

  const isTotalActive = activeFilter === 'total';
  const isPastDueActive = activeFilter === 'pastDue';
  const isUpcomingActive = activeFilter === 'upcoming';
  const isInProgressActive = activeFilter === 'inProgress';

  return (
    <Grid container spacing={2}>
      {/* Total Tasks Card */}
      {!hideTotal && (
      <Grid item {...cardGridSize}>
        <Card
          sx={{
            cursor: 'pointer',
            bgcolor: isTotalActive ? 'primary.light' : 'background.paper',
            border: isTotalActive ? 2 : 1,
            borderColor: isTotalActive ? 'primary.main' : 'divider',
            '&:hover': {
              bgcolor: 'action.hover',
              transform: 'translateY(-2px)',
              boxShadow: 2,
            },
            transition: 'all 0.2s',
          }}
          onClick={() => handleCardClick('total')}
        >
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {summary.totalTasks}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Assignment fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Total Tasks
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      )}

      {/* Past Due Card */}
      <Grid item {...cardGridSize}>
        <Card
          sx={{
            cursor: 'pointer',
            bgcolor: isPastDueActive ? 'error.light' : 'background.paper',
            border: isPastDueActive ? 2 : 1,
            borderColor: isPastDueActive ? 'error.main' : 'divider',
            '&:hover': {
              bgcolor: 'error.light',
              transform: 'translateY(-2px)',
              boxShadow: 2,
            },
            transition: 'all 0.2s',
          }}
          onClick={() => handleCardClick('pastDue')}
        >
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: 'error.main' }}>
              {summary.pastDueCount}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Warning fontSize="small" color="error" />
              <Typography variant="body2" color="text.secondary">
                Past Due
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Upcoming Card */}
      {!hideUpcoming && (
      <Grid item {...cardGridSize}>
        <Card
          sx={{
            cursor: 'pointer',
            bgcolor: isUpcomingActive ? 'warning.light' : 'background.paper',
            border: isUpcomingActive ? 2 : 1,
            borderColor: isUpcomingActive ? 'warning.main' : 'divider',
            '&:hover': {
              bgcolor: 'warning.light',
              transform: 'translateY(-2px)',
              boxShadow: 2,
            },
            transition: 'all 0.2s',
          }}
          onClick={() => handleCardClick('upcoming')}
        >
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: 'warning.main' }}>
              {summary.upcomingCount}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <CalendarToday fontSize="small" color="warning" />
              <Typography variant="body2" color="text.secondary">
                Upcoming
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      )}

      {/* In Progress Card */}
      <Grid item {...cardGridSize}>
        <Card
          sx={{
            cursor: 'pointer',
            bgcolor: isInProgressActive ? 'success.light' : 'background.paper',
            border: isInProgressActive ? 2 : 1,
            borderColor: isInProgressActive ? 'success.main' : 'divider',
            '&:hover': {
              bgcolor: 'success.light',
              transform: 'translateY(-2px)',
              boxShadow: 2,
            },
            transition: 'all 0.2s',
          }}
          onClick={() => handleCardClick('inProgress')}
        >
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: 'success.main' }}>
              {summary.inProgressCount}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Sync fontSize="small" color="success" />
              <Typography variant="body2" color="text.secondary">
                In Progress
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

